import { useState } from 'react';
import {
  Card, CardBody, Table, Button, Badge, Alert, Spinner,
  Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { useHasPermission } from '../../rbac/useHasPermission';
import { useOutboundSenders, useSetOverride, useClearOverride } from './useOutboundSenders';

/**
 * #561 Outbound-Absender-Management. Zeigt pro authentifiziertem Absender das
 * GELERNTE Sendeprofil (#559) samt aktueller Menge und effektiver Decke; ein
 * Operator kann das Limit manuell anheben, den Absender ausnehmen (exempt) oder
 * den Override entfernen. Backend: orbit-backend /v1/spam/outbound/senders*.
 */
const fmtNum = (n) => (Number.isFinite(Number(n)) ? Number(n).toLocaleString('de-DE') : '—');
const fmtDate = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('de-DE');
};

function OverrideBadge({ override }) {
  if (!override) return <span className="text-muted">—</span>;
  if (override.exempt) return <Badge color="info">ausgenommen</Badge>;
  if (override.manualHourlyCap != null) return <Badge color="warning">Limit {fmtNum(override.manualHourlyCap)}/h</Badge>;
  return <span className="text-muted">—</span>;
}

export function OutboundSendersPage() {
  const canWrite = useHasPermission({ perms: ['integrations.write'] });
  const q = useOutboundSenders();
  const setOverride = useSetOverride();
  const clearOverride = useClearOverride();

  const [edit, setEdit] = useState(null); // { sender, cap, exempt, note }
  const senders = q.data?.senders ?? [];
  const busy = setOverride.isPending || clearOverride.isPending;

  const openEdit = (s) => setEdit({
    sender: s,
    cap: s.override?.manualHourlyCap ?? '',
    exempt: !!s.override?.exempt,
    note: s.override?.note ?? '',
  });

  const save = () => {
    const body = {
      manual_hourly_cap: edit.cap === '' || edit.cap == null ? null : Number(edit.cap),
      exempt: edit.exempt,
      note: edit.note,
    };
    setOverride.mutate({ key: edit.sender.senderKey, body }, { onSuccess: () => setEdit(null) });
  };

  return (
    <div>
      <PageHeader
        title="Ausgehende Absender"
        subtitle="Velocity-/Volumen-Schutz (#559) — gelerntes Sendeprofil je Absender; Limits manuell anheben oder freigeben"
      />
      <Alert color="info" className="small">
        Die Schwelle wird <strong>pro Absender gelernt</strong> (Abweichung vom eigenen Profil = Anomalie) — legitime
        Newsletter-/Bulk-Versender werden dadurch nicht gedrosselt. <strong>Limit anheben</strong> hebt die effektive
        Decke, <strong>Ausnehmen</strong> schaltet die Velocity-Prüfung für diesen Absender ab. Änderungen werden im
        Audit-Log erfasst.
      </Alert>

      <div className="d-flex justify-content-end align-items-center mb-2 gap-2">
        <span className="text-muted small">{senders.length} Absender</span>
        <Button size="sm" color="secondary" outline disabled={q.isFetching} onClick={() => q.refetch()}>
          {q.isFetching ? 'Aktualisiere…' : 'Aktualisieren'}
        </Button>
      </div>

      <Card>
        <CardBody className="p-0">
          {q.isLoading ? (
            <div className="p-4 text-center"><Spinner size="sm" /> Lade Absender…</div>
          ) : q.isError ? (
            <Alert color="danger" className="m-3 small">Konnte Absender nicht laden: {String(q.error?.message || q.error)}</Alert>
          ) : senders.length === 0 ? (
            <div className="p-4 text-center text-muted">Noch keine Absender-Aktivität erfasst.</div>
          ) : (
            <Table responsive hover className="mb-0 align-middle small">
              <thead>
                <tr>
                  <th>Absender</th>
                  <th className="text-end">Gelernt (Ø · Decke)</th>
                  <th className="text-end">1h</th>
                  <th className="text-end">24h</th>
                  <th className="text-end">Effektive Decke</th>
                  <th>Override</th>
                  <th className="text-end">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {senders.map((s) => {
                  const over = s.effectiveCap != null && s.msgsThisHour > s.effectiveCap;
                  return (
                    <tr key={s.senderKey}>
                      <td>
                        <code className="small">{s.label}</code>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>seit {fmtDate(s.firstSeen)}</div>
                      </td>
                      <td className="text-end text-muted">
                        {fmtNum(Math.round(s.learned?.mean))} · {fmtNum(s.learned?.ceiling)}/h
                      </td>
                      <td className={`text-end ${over ? 'text-danger fw-bold' : ''}`}>{fmtNum(s.msgsThisHour)}</td>
                      <td className="text-end text-muted">{fmtNum(s.msgs24h)}</td>
                      <td className="text-end">{s.effectiveCap == null ? <span className="text-muted">—</span> : `${fmtNum(s.effectiveCap)}/h`}</td>
                      <td><OverrideBadge override={s.override} /></td>
                      <td className="text-end text-nowrap">
                        <Button size="sm" color="secondary" outline disabled={!canWrite || busy} onClick={() => openEdit(s)}>
                          {s.override ? 'Override ändern' : 'Limit anheben / freigeben'}
                        </Button>
                        {s.override && (
                          <Button size="sm" color="link" className="text-danger" disabled={!canWrite || busy}
                            onClick={() => clearOverride.mutate({ key: s.senderKey })}>
                            Entfernen
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      <Modal isOpen={!!edit} toggle={() => setEdit(null)}>
        <ModalHeader toggle={() => setEdit(null)}>
          Override — <code>{edit?.sender?.label}</code>
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="ov-cap" className="small fw-bold">Manuelles Stundenlimit</Label>
            <Input id="ov-cap" type="number" min="1" value={edit?.cap ?? ''} disabled={edit?.exempt}
              placeholder="z. B. 2000 (leer = keine Anhebung)"
              onChange={(e) => setEdit((s) => ({ ...s, cap: e.target.value }))} />
            <span className="text-muted small">Hebt die effektive Decke auf mindestens diesen Wert (überschreibt die gelernte Schwelle nicht nach unten).</span>
          </FormGroup>
          <FormGroup check className="mb-3">
            <Input id="ov-exempt" type="checkbox" checked={!!edit?.exempt}
              onChange={(e) => setEdit((s) => ({ ...s, exempt: e.target.checked }))} />
            <Label for="ov-exempt" check>Von der Velocity-Prüfung ausnehmen (exempt)</Label>
          </FormGroup>
          <FormGroup>
            <Label for="ov-note" className="small fw-bold">Notiz</Label>
            <Input id="ov-note" type="textarea" rows="2" value={edit?.note ?? ''}
              placeholder="Grund / Ticket-Referenz"
              onChange={(e) => setEdit((s) => ({ ...s, note: e.target.value }))} />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setEdit(null)} disabled={busy}>Abbrechen</Button>
          <Button color="primary" onClick={save} disabled={busy || !canWrite}>
            {setOverride.isPending ? 'Speichere…' : 'Speichern'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default OutboundSendersPage;
